import React, { useEffect, useRef, useState } from "react";
import { Form, Input, Select, Button, Row, Col, Avatar } from "antd";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";
import Main from "../../components/layout/Main";
import ProfileImage from "../../assets/images/profile-image.png";
import { Camera } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "../../context/AuthContext";
import TextArea from "antd/lib/input/TextArea";
import { Severty, ShowToast } from "../../helper/toast";

const { Option } = Select;
const strongPwdRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/~`]).{6,}$/;

const EditProfileIndex = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState();
  const [fileError, setFileError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const { getProfile, updateProfile, changePassword } = useAuth();
  const [profile, setProfile] = useState();

  // --------- handlers ----------
  const handleImageChange = (e) => {
    const file = e.target.files[0];

    const isValidType = ["image/jpeg", "image/png", "image/jpg"].includes(
      file.type
    );

    if (!isValidType) {
      setFileError("Invalid file type! Only JPG, JPEG and PNG are allowed.");
      return;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      setFileError("Image must be smaller than 5MB!");
      return;
    }
    // clear previous error
    setFileError(null);

    // Revoke previous preview URL if exists
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    // Generate a local preview URL
    const localUrl = URL.createObjectURL(file);
    setPreviewUrl(localUrl);

    setAvatar(file);
  };

  // --------- fetch profile -----------
  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      if (response.status === 200) {
        setProfile(response.data);
      }
    } catch (error) {
      console.log("Error Ocurred when fetch profile", error);
    }
  };

  // --------- update profile -----------
  const handleUpdateProfile = async (values) => {
    try {
      const formData = new FormData();

      // Only append avatar if it exists
      if (avatar) {
        formData.append("profilePic", avatar);
      }

      // Append all form fields
      const fields = [
        "name",
        "bio",
        "username",
        "instagramLink",
        "facebookLink",
        "tikTokLink",
        "xLink",
        "location",
        "city",
        "country",
        "gender",
        "activityLogsTo",
        "gamingPlartform",
      ];

      fields.forEach((field) => {
        if (values[field] !== undefined && values[field] !== null) {
          formData.append(field, values[field]);
        }
      });

      const response = await updateProfile(formData);
      if (response.status === 200) {
        ShowToast(response.message, Severty.SUCCESS);
        navigate("/profile");
      } else {
        ShowToast(response.message, Severty.ERROR);
      }
    } catch (error) {
      console.log("Error Ocurred when update profile", error);
    }
  };

  // --------- Update Password -----------
  const handleUpdatePassword = async (values) => {
    try {
      const data = {
        old_password: values.currentPassword,
        new_password: values.newPassword,
      };
      const response = await changePassword(data);
      if (response.status === 200) {
        ShowToast(response.message, Severty.SUCCESS);
        navigate("/profile");
      }
    } catch (error) {
      ShowToast(error.message, Severty.ERROR);
    }
  };

  // ---------- use effect -----------
  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      name: profile?.name,
      username: profile?.username,
      xLink: profile?.xLink,
      instagramLink: profile?.instagramLink,
      facebookLink: profile?.facebookLink,
      tikTokLink: profile?.tikTokLink,
      location: profile?.location,
      city: profile?.city,
      country: profile?.country,
      gender: profile?.gender,
      activityLogsTo: profile?.activityLogsTo,
      gamingPlartform: profile?.gamingPlartform,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      profilePic: profile?.profilePic,
      bio: profile?.bio,
      userId: profile?.userId,
    });
  }, [profile]);

  // Set preview to existing profile picture when profile loads
  useEffect(() => {
    if (profile?.profilePic && !previewUrl) {
      setPreviewUrl(profile.profilePic);
    }
  }, [profile, previewUrl]);

  // Cleanup object URL on unmount or when preview changes
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  return (
    <Main>
      <div className="edit-profile-container section-main">
        <h6 className="orange-header">Edit Profile</h6>

        <div className="edit-profile-avatar">
          <img
            src={previewUrl || ProfileImage}
            alt="Avatar"
            className="avatar-image"
          />
          <div
            className="camera-icon"
            onClick={() => fileInputRef.current.click()}
          >
            <Camera />
          </div>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
        </div>
        {!fileError && avatar && (
          <p className="profile-file-error" style={{ color: "green" }}>
            {avatar.name}
          </p>
        )}
        {fileError && (
          <p className="profile-file-error" style={{ color: "red" }}>
            {fileError}
          </p>
        )}

        <Row gutter={32}>
          {/* Left Section */}
          <Col xs={24} md={12}>
            <Form
              layout="vertical"
              form={form}
              className="edit-profile-form"
              onFinish={handleUpdateProfile}
            >
              <Form.Item
                label="Full Name"
                name="name"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.trim()) {
                        return Promise.reject(
                          new Error("Please enter your full name")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input className="profile-input" placeholder="Alex Morgan" />
              </Form.Item>

              <Form.Item label="Bio" name="bio" className="text-white">
                <TextArea
                  className="profile-input "
                  placeholder="Enter your bio"
                />
              </Form.Item>

              <Form.Item
                // value={profile?.userId}
                label="User ID"
                name="userId"
                disabled
              >
                <Input
                  className="profile-input"
                  placeholder="Enter your username"
                />
              </Form.Item>

              <Form.Item
                label="Username"
                name="username"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.trim()) {
                        return Promise.reject(
                          new Error("Please enter your full name")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className="profile-input"
                  placeholder="Enter your username"
                />
              </Form.Item>

              <Form.Item label="City" name="city">
                <Input className="profile-input" placeholder="Kuwait City" />
              </Form.Item>

              <Form.Item label="Country" name="country">
                <Input className="profile-input" placeholder="Kuwait" />
              </Form.Item>

              <Form.Item label="Gender" name="gender">
                <Select className="profile-select" placeholder="Select Gender">
                  <Option value="Male">Male</Option>
                  <Option value="Female">Female</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Location" name="location">
                <Input
                  className="profile-input"
                  placeholder="Kuwait City, Kuwait"
                />
              </Form.Item>

              <Form.Item label="Show Activity Logs To" name="activityLogsTo">
                <Select
                  className="profile-select"
                  placeholder="Select Activity Log to"
                >
                  <Option value="friends">Friends</Option>
                  <Option value="publicToAll">Public To All</Option>
                </Select>
              </Form.Item>

              <Form.Item label="X Link" name="xLink">
                <Input
                  className="profile-input"
                  placeholder="Enter your x link"
                />
              </Form.Item>

              <Form.Item label="Instagram Link" name="instagramLink">
                <Input
                  className="profile-input"
                  placeholder="Enter your instagram link"
                />
              </Form.Item>

              <Form.Item label="Facebook Link" name="facebookLink">
                <Input
                  className="profile-input"
                  placeholder="Enter your facebook link"
                />
              </Form.Item>

              <Form.Item label="Tik Tok Link" name="tikTokLink">
                <Input
                  className="profile-input"
                  placeholder="Enter your tik tok link"
                />
              </Form.Item>

              <Form.Item label="Select Gaming Platform" name="gamingPlartform">
                <Select
                  className="profile-select"
                  placeholder="Select Gaming Platform"
                >
                  <Option value="console">Console</Option>
                  <Option value="pc">PC</Option>
                </Select>
              </Form.Item>

              <div className="edit-profile-actions">
                <Button
                  className="social-btn"
                  onClick={() => navigate("/profile")}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="fill-button-smal"
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </Col>
          {/* Update Password Form */}
          {/* Right Section */}
          <Col xs={24} md={12} onFinish={handleUpdatePassword}>
            <Form layout="vertical" onFinish={handleUpdatePassword}>
              <Form.Item
                label="Current Password"
                name="currentPassword"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.trim()) {
                        return Promise.reject(
                          new Error("Current password is required")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className="profile-input"
                  placeholder="Enter current password"
                />
              </Form.Item>

              <Form.Item
                label="New Password"
                name="newPassword"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || !value.trim()) {
                        return Promise.reject(
                          new Error("New password is required")
                        );
                      }
                      if (!strongPwdRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Password must be at least 6 characters and include uppercase, lowercase, a number and a special character."
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className="profile-input"
                  placeholder="Enter new password"
                />
              </Form.Item>

              <Form.Item
                label="Confirm New Password"
                name="confirmPassword"
                rules={[
                  {
                    required: true,
                    validator: (_, value) => {
                      if (!value || !value.trim()) {
                        return Promise.reject(
                          new Error("Confirm new password is required")
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("newPassword") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Passwords do not match")
                      );
                    },
                  }),
                ]}
              >
                <Input
                  className="profile-input"
                  placeholder="Re-enter new password"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="default"
                  htmlType="submit"
                  className="Fill-button-small save-btn-main"
                  // onClick={() => navigate("/profile")}
                >
                  Update Password
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </div>
    </Main>
  );
};

export default EditProfileIndex;
